import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Slider from '../BrandSection/Slider/Slider';
import { useTranslation } from 'react-i18next';
import HistorySkeleton from './HistorySkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../AnimatedOnScroll';
import './History.scss';

const History = ({ pageKey }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  
  const { data: historyJson, loading: dataLoading, error: dataError, refetch } = useCachedFetch('/data/locales/history.json');

  const handleRetry = useCallback(() => {
    refetch();
  }, [refetch]);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (dataLoading) return;

    if (dataError) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const data = historyJson?.HistoryData?.[currentLang]?.[pageKey] || historyJson?.HistoryData?.en?.[pageKey];

    if (data && data.historySection) {
      setHasError(false);
    } else {
      setHasError(true);
    }

    setIsLoading(false);
  }, [dataLoading, dataError, historyJson, pageKey, currentLang]);

  const historyData = useMemo(() => {
    return historyJson?.HistoryData?.[currentLang]?.[pageKey] || historyJson?.HistoryData?.en?.[pageKey] || null;
  }, [historyJson, currentLang, pageKey]);

  const parseSimpleSpans = useCallback((text) => {
    if (typeof text !== 'string') return text;

    const parts = [];
    let currentIndex = 0;
    const regex = /<span>([^<]+)<\/span>/g;
    
    let match;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > currentIndex) {
        parts.push(text.substring(currentIndex, match.index));
      }

      parts.push(
        <span key={match.index} className="text-bold">
          {match[1]}
        </span>
      );
      
      currentIndex = match.index + match[0].length;
    }
    
    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }
    
    return parts;
  }, []);

  const historySection = useMemo(() => {
    return historyData?.historySection || null;
  }, [historyData]);

  const hasReviews = useMemo(() => 
    historySection?.reviews,
    [historySection]
  );

  if (isLoading) {
    return <HistorySkeleton />;
  }
  if (hasError || !historyData || !historySection) {
    return <ErrorState onRetry={handleRetry} />;
  }

  return (
    <div className='about__history__section__wrapper'>
      <div className='about__history__left__section'>
        <AnimatedOnScroll animation="fade-up" delay={0}>
        <h4 className='about__history__left__section__title'>
          {historySection.title} <span>{historySection.titleSpan}</span>
        </h4>
        </AnimatedOnScroll>
        {historySection.paragraphs.map((text, index) => (
          <AnimatedOnScroll animation="fade-up" delay={100} key={index}>
          <p key={index} className='about__history__left__section__text'>
            {parseSimpleSpans(text)}
          </p>
          </AnimatedOnScroll>
        ))}
        <AnimatedOnScroll animation="fade-up" delay={100}>
        <p className='about__history__left__section__text--primary'>
          {parseSimpleSpans(historySection.primaryText)}
        </p>
        </AnimatedOnScroll>
      </div>
      
      {hasReviews && (
        <div className='about__history__right__section'>
          <AnimatedOnScroll animation="fade-up" delay={0}>
          <h4 className='clients__title'>{historySection.reviews}</h4>
          </AnimatedOnScroll>
          <Slider/>
        </div>
      )}
    </div>
  );
};

export default React.memo(History);